import React, { forwardRef, useMemo } from "react";
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  Table,
} from "@mui/material";

const LabExternalPrint = forwardRef(
  ({ reportData, patientData, selectedDate }, ref) => {
    // ✅ reportData should be: apiResponse.medical_reports
    const reports = reportData || [];

    const { normalTests, compositeParents } = useMemo(() => {
      const normal = [];
      const parents = [];

      for (const item of reports) {
        const hasComposites =
          Array.isArray(item?.composites) && item.composites.length > 0;
        if (hasComposites) parents.push(item);
        else normal.push(item);
      }

      return { normalTests: normal, compositeParents: parents };
    }, [reports]);

    if (!reportData) return null;

    // console.log(normalTests, "normalTests");
    // console.log(compositeParents, "compositeParents");
    return (
      <Box
        ref={ref}
        sx={{
          padding: "20px",
          width: "96%",
          background: "#fff",
          fontFamily: "Lato",
          display: "none",
          "@page": { marginTop: `2in`, marginBottom: `1in` },
          "@media print": {
            display: "block",
            "& table": { pageBreakInside: "avoid" },
          },
        }}
      >
        <Typography variant="heading_Large">{`${patientData?.full_name} (${patientData?.uid})`}</Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "15px 2px 5px 2px",
          }}
        >
          <Typography sx={{ fontWeight: "bold" }} fontSize={"14px"}>
            {patientData?.full_name}
          </Typography>

          <Typography sx={{ fontWeight: "bold" }} fontSize={"14px"}>
            Mob: {patientData?.mobile}
          </Typography>

          <Typography sx={{ fontWeight: "bold" }} fontSize={"14px"}>
            {patientData?.age === 0 ? 1 : patientData?.age} /{" "}
            {patientData?.gender}
          </Typography>

          <Typography sx={{ fontWeight: "bold" }} fontSize={"14px"}>
            {`Report Date : ${selectedDate}`}
          </Typography>
        </Box>

        {/* ===================== NORMAL TESTS TABLE (FIRST TABLE) ===================== */}
        <Table
          sx={{
            border: "1px solid lightgrey",
            borderBottom: "1.9px solid grey",
            width: "100%",
            mb: "1rem !important",
          }}
        >
          {normalTests?.length > 0 && (
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", width: "34%", padding: "6px" }}
                >
                  Test Name
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", width: "30%", padding: "6px" }}
                >
                  Result
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", width: "30%", padding: "6px" }}
                >
                  Reference Range
                </TableCell>
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {normalTests?.length > 0 &&
              normalTests.map((test, index) => (
                <TableRow key={index} sx={{ width: "100%" }}>
                  <TableCell
                    align="center"
                    sx={{ width: "35%", padding: "4px" }}
                  >
                    {test?.name || "-"}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ width: "32%", padding: "4px" }}
                  >
                    {test?.value ?? "-"}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ width: "32%", padding: "4px" }}
                  >
                    {patientData?.gender === "Female"
                      ? test?.reference_range?.female_min_max || "-"
                      : test?.reference_range?.male_min_max || "-"}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        {/* ===================== COMPOSITE TABLES (SEPARATE TABLE FOR EACH PARENT) ===================== */}
        {compositeParents?.length > 0 &&
          [
            ...new Set(
              compositeParents.map(
                (parent) =>
                  parent?.parent_test_name ||
                  parent?.parent_test_code ||
                  "Composite Test",
              ),
            ),
          ].map((parentName, index) => (
            <React.Fragment key={index}>
              <Table
                sx={{
                  marginTop: "100px",
                  border: "1px solid lightgrey",
                  borderBottom: "1.9px solid grey",
                  textAlign: "center",
                  width: "100%",
                  mb: "1rem !important",
                }}
              >
                <TableBody>
                  {/* Header Row (same like your UI) */}
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography sx={{ fontWeight: "bold" }}>
                        {parentName}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Composite rows */}
                  {compositeParents
                    .filter(
                      (p) =>
                        (p?.parent_test_name ||
                          p?.parent_test_code ||
                          "Composite Test") === parentName,
                    )
                    .flatMap((p) => p?.composites || [])
                    .map((comp, idx) => (
                      <TableRow key={idx}>
                        <TableCell
                          sx={{
                            width: "33%",
                            padding: "4px",
                            textAlign: "center",
                          }}
                        >
                          {comp?.name || "-"}
                        </TableCell>

                        <TableCell
                          sx={{
                            width: "33%",
                            padding: "4px",
                            textAlign: "center",
                          }}
                        >
                          {comp?.value ?? "-"}
                        </TableCell>

                        <TableCell
                          sx={{
                            width: "33%",
                            padding: "4px",
                            textAlign: "center",
                          }}
                        >
                          {patientData?.gender === "Female"
                            ? comp?.reference_range?.female_min_max || "-"
                            : comp?.reference_range?.male_min_max || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </React.Fragment>
          ))}
      </Box>
    );
  },
);

export default LabExternalPrint;
